
import { Component, OnInit, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatChipsModule } from '@angular/material/chips';
import { MatDividerModule } from '@angular/material/divider';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { Subject } from 'rxjs';
import { FeatureFlag } from '../../../models/feature-flag.models';
import { AuthService } from '../../../services/auth.service';
import { FeatureFlagService } from '../../../services/feature-flag.service';
import { ErrorStateComponent } from '../../shared/error-state/error-state.component';

@Component({
  selector: 'app-feature-flags',
  standalone: true,
  imports: [
    FormsModule,
    MatButtonModule,
    MatCardModule,
    MatChipsModule,
    MatDividerModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatProgressSpinnerModule,
    MatSlideToggleModule,
    ErrorStateComponent
],
  templateUrl: './feature-flags.component.html',
  styleUrls: ['./feature-flags.component.scss'],
})
export class FeaturesComponent implements OnInit {
  private featureFlagService = inject(FeatureFlagService);
  private authService = inject(AuthService);

  featureFlags: FeatureFlag[] = [];
  filteredFeatureFlags: FeatureFlag[] = [];
  isLoading = true;
  error: string | null = null;
  accessDenied = false;
  hasSuperAdminAccess = false;

  // Search
  searchQuery = '';
  private searchDebounceTimer: ReturnType<typeof setTimeout> | null = null;

  private destroy$ = new Subject<void>();

  ngOnInit(): void {
    this.checkAccess();
  }

  private applyFilter(searchTerm: string): void {
    const term = searchTerm?.toLowerCase().trim() || '';

    if (!term) {
      this.filteredFeatureFlags = [...this.featureFlags];
    } else {
      this.filteredFeatureFlags = this.featureFlags.filter(
        (flag) =>
          flag.name.toLowerCase().includes(term) ||
          flag.description?.toLowerCase().includes(term),
      );
    }
  }

  private checkAccess(): void {
    // Check if user has the permission to view feature flag management
    this.hasSuperAdminAccess = this.authService.canViewFeatureFlagManagement();

    if (this.hasSuperAdminAccess) {
      this.loadFeatureFlags();
    } else {
      this.accessDenied = true;
      this.isLoading = false;
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  loadFeatureFlags(): void {
    this.isLoading = true;
    this.error = null;

    this.featureFlagService.getAllFeatureFlags().subscribe({
      next: (flags) => {
        this.featureFlags = flags;
        this.applyFilter(this.searchQuery);
        this.isLoading = false;
      },
      error: (error) => {
        console.error('Error loading feature flags:', error);
        this.error = 'Failed to load feature flags. Please try again.';
        this.isLoading = false;
      },
    });
  }

  toggleFeatureFlag(flag: FeatureFlag): void {
    // Optimistically update the UI
    flag.isEnabled = !flag.isEnabled;

    this.featureFlagService.toggleFeatureFlag(flag.id).subscribe({
      next: (success) => {
        if (!success) {
          // Revert the toggle if the API call failed
          flag.isEnabled = !flag.isEnabled;
          console.error('Failed to toggle feature flag');
        }
        // The service's cache management will handle updating other components
        // No need to reload all feature flags
      },
      error: (error) => {
        console.error('Error toggling feature flag:', error);
        // Revert the toggle on error
        flag.isEnabled = !flag.isEnabled;
      },
    });
  }

  formatRelativeDate(dateString: string | null): string {
    return this.featureFlagService.formatRelativeDate(dateString);
  }

  onSearchInput(): void {
    if (this.searchDebounceTimer) {
      clearTimeout(this.searchDebounceTimer);
    }
    this.searchDebounceTimer = setTimeout(() => {
      this.applyFilter(this.searchQuery);
    }, 300);
  }

  clearSearch(): void {
    this.searchQuery = '';
    this.applyFilter('');
  }

  trackByFlagId(index: number, flag: FeatureFlag): number {
    return flag.id;
  }
}
